import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, KeyRound, Download, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SectionCard, SectionTitle, InfoBox } from "@/components/ui-kit";
import { GuidedTip } from "@/components/GuidedTip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { languages, currencies } from "@/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputClass = "bg-brand-ivory/50 border-brand-line rounded-xl h-auto py-3 focus-visible:ring-2 focus-visible:ring-brand-champagne";

export default function SettingsPage() {
  const { t, user, updateProfile, changeLanguage, logout, changePassword, deleteAccount, exportData, config } = useAuth();
  const navigate = useNavigate();
  const [hourly, setHourly] = useState(user.hourly_value ?? 0);
  const [vatEnabled, setVatEnabled] = useState(!!user.vat_enabled);
  const [vatRate, setVatRate] = useState(user.vat_rate ?? 0);
  const [currency, setCurrency] = useState(user.currency || "EUR");
  const [studio, setStudio] = useState(user.studio_name ?? "");
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ open: false, current: "", next: "", confirm: "" });
  const [del, setDel] = useState({ open: false, password: "" });
  const [busy, setBusy] = useState(false);

  const doChangePassword = async () => {
    if (pwd.next !== pwd.confirm) { toast.error(t("pro.passwordMismatch")); return; }
    setBusy(true);
    try { await changePassword(pwd.current, pwd.next); toast.success(t("pro.passwordChanged")); setPwd({ open: false, current: "", next: "", confirm: "" }); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  const doDelete = async () => {
    setBusy(true);
    try { await deleteAccount(del.password); toast.success(t("pro.accountDeleted")); navigate("/login"); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setBusy(false); }
  };
  const doExport = async () => { try { await exportData(); } catch (err) { toast.error(err.response?.data?.detail || err.message); } };

  const save = async () => {
    setSaving(true);
    try {
      await updateProfile({
        hourly_value: Number(hourly) || 0,
        vat_enabled: vatEnabled,
        vat_rate: Number(vatRate) || 0,
        currency,
        studio_name: studio,
      });
      localStorage.setItem("currency", currency);
      toast.success(t("settings.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="space-y-6">
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brand-anthracite">{t("settings.title")}</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <SectionCard>
          <SectionTitle>{t("settings.profile")}</SectionTitle>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>{t("settings.language")}</Label>
              <Select value={user.language} onValueChange={changeLanguage}>
                <SelectTrigger className={inputClass} data-testid="settings-language"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("settings.currency")}</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className={inputClass} data-testid="settings-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              {currency !== user.currency && (
                <p className="text-xs text-[#8a5a24] bg-[#C78238]/10 border border-[#C78238]/30 rounded-lg px-3 py-2" data-testid="settings-currency-note">{t("settings.currencyNote")}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t("settings.hourlyValue")}</Label>
              <div className="relative max-w-[200px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-taupe">{currency === "GBP" ? "£" : "€"}</span>
                <Input type="number" min="0" step="0.5" value={hourly} onChange={(e) => setHourly(e.target.value)} className={`${inputClass} pl-8`} data-testid="settings-hourly" />
              </div>
              <p className="text-xs text-brand-taupe">{t("settings.hourlyNote")}</p>
            </div>
            <GuidedTip id="hourly-settings">{t("tips.hourly")}</GuidedTip>
            <div className="space-y-2">
              <Label>{t("settings.studioName")} <span className="text-brand-taupe font-normal">({t("common.optional")})</span></Label>
              <Input value={studio} onChange={(e) => setStudio(e.target.value)} className={inputClass} data-testid="settings-studio" maxLength={60} />
            </div>
          </div>
        </SectionCard>

        <SectionCard>
          <SectionTitle>{t("settings.vatSection")}</SectionTitle>
          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-xl border border-brand-line px-4 py-3">
              <span className="font-medium">{t("settings.vatEnabled")}</span>
              <Switch checked={vatEnabled} onCheckedChange={setVatEnabled} data-testid="settings-vat-switch" />
            </div>
            {vatEnabled && (
              <div className="space-y-2">
                <Label>{t("settings.vatRate")}</Label>
                <Input type="number" min="0" max="100" value={vatRate} onChange={(e) => setVatRate(e.target.value)} className={`${inputClass} max-w-[140px]`} data-testid="settings-vat-rate" />
              </div>
            )}
            <p className="text-xs text-brand-taupe">{t("settings.vatNote")}</p>
          </div>
        </SectionCard>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Button onClick={save} disabled={saving} data-testid="settings-save" className="rounded-xl bg-brand-anthracite text-brand-ivory hover:bg-brand-anthracite/90 h-12 px-8">
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </div>

      <SectionCard>
        <SectionTitle>{t("settings.account")}</SectionTitle>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><div className="text-xs uppercase tracking-wider text-brand-taupe">{t("settings.email")}</div><div className="font-medium text-brand-anthracite">{user.email}</div></div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setPwd({ ...pwd, open: true })} data-testid="settings-change-password" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand"><KeyRound className="mr-2 h-4 w-4" />{t("pro.changePassword")}</Button>
            <Button variant="outline" onClick={doExport} data-testid="settings-export" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand"><Download className="mr-2 h-4 w-4" />{t("pro.exportData")}</Button>
            <Button variant="outline" onClick={handleLogout} data-testid="settings-logout" className="rounded-xl border-brand-line text-brand-anthracite hover:bg-brand-sand"><LogOut className="mr-2 h-4 w-4" />{t("auth.logout")}</Button>
          </div>
        </div>
        <div className="mt-6 border-t border-[#B85B5B]/20 pt-4">
          <div className="text-xs uppercase tracking-wider text-[#B85B5B]">{t("pro.dangerZone")}</div>
          <Button variant="outline" onClick={() => setDel({ open: true, password: "" })} data-testid="settings-delete-account" className="mt-2 rounded-xl border-[#B85B5B]/40 text-[#B85B5B] hover:bg-[#B85B5B]/10"><Trash2 className="mr-2 h-4 w-4" />{t("pro.deleteAccount")}</Button>
        </div>
      </SectionCard>

      {(config.terms_url || config.privacy_url || config.website_url || config.support_url) && (
        <SectionCard>
          <SectionTitle>{t("pro.legalTitle")}</SectionTitle>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {config.terms_url && <a href={config.terms_url} target="_blank" rel="noreferrer" className="text-brand-anthracite underline decoration-brand-champagne" data-testid="legal-terms">{t("pro.terms")}</a>}
            {config.privacy_url && <a href={config.privacy_url} target="_blank" rel="noreferrer" className="text-brand-anthracite underline decoration-brand-champagne" data-testid="legal-privacy">{t("pro.privacy")}</a>}
            {config.website_url && <a href={config.website_url} target="_blank" rel="noreferrer" className="text-brand-anthracite underline decoration-brand-champagne" data-testid="legal-website">{t("pro.website")}</a>}
            {config.support_url && <a href={config.support_url} target="_blank" rel="noreferrer" className="text-brand-anthracite underline decoration-brand-champagne" data-testid="legal-support">{t("pro.support")}</a>}
          </div>
        </SectionCard>
      )}

      <InfoBox title={t("settings.disclaimerTitle")}>{t("settings.disclaimerBody")}</InfoBox>

      <Dialog open={pwd.open} onOpenChange={(o) => setPwd({ ...pwd, open: o })}>
        <DialogContent className="bg-white border-brand-line max-w-sm" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle className="font-serif">{t("pro.changePassword")}</DialogTitle>
            <DialogDescription className="text-brand-taupe">{t("settings.account")}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>{t("pro.currentPassword")}</Label><Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className={inputClass} data-testid="current-password" /></div>
            <div className="space-y-1"><Label>{t("pro.newPassword")}</Label><Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className={inputClass} data-testid="new-password" /></div>
            <div className="space-y-1"><Label>{t("pro.confirmPassword")}</Label><Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className={inputClass} data-testid="confirm-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwd({ ...pwd, open: false })} className="rounded-xl border-brand-line">{t("common.cancel")}</Button>
            <Button onClick={doChangePassword} disabled={busy} data-testid="change-password-submit" className="rounded-xl bg-brand-anthracite text-brand-ivory">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={del.open} onOpenChange={(o) => setDel({ ...del, open: o })}>
        <DialogContent className="bg-white border-brand-line max-w-sm" data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="font-serif">{t("pro.deleteAccount")}</DialogTitle>
            <DialogDescription className="text-[#8a4141]">{t("pro.deleteAccountWarn")}</DialogDescription></DialogHeader>
          <div className="space-y-1">
            <Label>{t("pro.deleteConfirmPassword")}</Label>
            <Input type="password" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} className={inputClass} data-testid="delete-account-password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel({ open: false, password: "" })} className="rounded-xl border-brand-line">{t("common.cancel")}</Button>
            <Button onClick={doDelete} disabled={busy || !del.password} data-testid="delete-account-submit" className="rounded-xl bg-[#B85B5B] text-white hover:bg-[#a24f4f]">{t("pro.deleteAccount")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
